import * as React from 'react'
import {
  TOOL_DEFINITIONS,
  TOOL_CLUSTERS,
  CLUSTER_ROUTES,
} from '@repo/studio-components/config/tool-definitions'
import type { ToolDefinition } from '@repo/studio-components/config/tool-definitions'
import { TOOL_ICONS } from '@repo/studio-components/config/tool-icons'
import type { NavItem } from './use-nav-items'
import type { NavItemType } from './use-menu-items'
import { studioBaseUrl } from '../utils/urls'

// Pre-compute static data at module level
const TOOLS_BY_CLUSTER: Record<string, ToolDefinition[]> = {}
for (const tool of TOOL_DEFINITIONS) {
  const list = TOOLS_BY_CLUSTER[tool.cluster] ?? []
  list.push(tool)
  TOOLS_BY_CLUSTER[tool.cluster] = list
}
for (const tools of Object.values(TOOLS_BY_CLUSTER)) {
  tools.sort((a, b) => a.order - b.order)
}

const SIZED_TOOL_ICONS: Record<string, React.ReactNode> = {}
for (const [key, icon] of Object.entries(TOOL_ICONS)) {
  SIZED_TOOL_ICONS[key] = React.cloneElement(icon, { className: 'h-4 w-4' })
}

type TranslateFn = (key: string, params?: Record<string, string | number | Date>) => string

export function useHomepageNavItems(t: TranslateFn): NavItem[] {
  return React.useMemo(() => {
    const clusterItems: NavItem[] = TOOL_CLUSTERS.map(cluster => {
      const tools = (TOOLS_BY_CLUSTER[cluster] ?? []).filter(tool => !tool.featureFlag)
      const clusterName = t(`navigation.studio_clusters.${cluster}`)

      return {
        id: cluster as NavItemType,
        name: clusterName,
        link: `${studioBaseUrl}${CLUSTER_ROUTES[cluster]}`,
        target: '_blank',
        subMenuItems: [
          {
            id: `${cluster}_tools` as NavItemType,
            name: `${clusterName} ${t('navigation.studio_clusters.tools_suffix')}`,
            link: `${studioBaseUrl}${CLUSTER_ROUTES[cluster]}`,
            target: '_blank',
            wrapItems: true,
            trending: {},
            subMenuItems: [
              ...tools.map(tool => ({
                name: t(`navigation.studio_tools.${tool.translationKey}`),
                link: `${studioBaseUrl}${tool.route}`,
                target: '_blank',
                icon: SIZED_TOOL_ICONS[tool.translationKey],
              })),
              {
                name: t('navigation.studio_clusters.view_all_tools', { cluster: clusterName }),
                link: `${studioBaseUrl}${CLUSTER_ROUTES[cluster]}`,
                target: '_blank',
                isFootOption: true,
              },
            ],
          },
        ],
      }
    }).filter(item => {
      const subItems = item.subMenuItems?.[0]?.subMenuItems ?? []
      return subItems.length > 1
    })

    const lumoItem: NavItem = {
      id: 'studio' as NavItemType,
      name: 'Lumo',
      link: `${studioBaseUrl}/lumo`,
      target: '_blank',
    }

    return [...clusterItems, lumoItem]
  }, [t])
}
